import React from 'react';

// CustomMapTypeControl component (moved to top of file for scope)

function CustomMapTypeControl({ mapInstance, inline = false }: { mapInstance: any, inline?: boolean }) {
  const controlRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState({ left: 0, top: 0 });

  // Only use positioning logic for floating (mobile) mode
  React.useEffect(() => {
    if (inline) return;
    function isTouchDevice() {
      return (typeof window !== 'undefined') && (('ontouchstart' in window) || (navigator.maxTouchPoints > 0));
    }
    function updatePosition() {
      const control = controlRef.current;
      const mapDiv = control?.parentElement;
      if (!control || !mapDiv) return;
      if (isTouchDevice()) {
        // Mobile: Center at the top of the map with 16px margin
        const mapRect = mapDiv.getBoundingClientRect();
        const controlRect = control.getBoundingClientRect();
        const left = Math.round((mapRect.width - controlRect.width) / 2);
        const top = 16; // px from top
        setPos({ left, top });
      }
    }
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [inline]);

  // Map type switching logic
  const [activeType, setActiveType] = React.useState('roadmap');
  React.useEffect(() => {
    if (!mapInstance) return;
    const listener = mapInstance.addListener('maptypeid_changed', () => {
      setActiveType(mapInstance.getMapTypeId());
    });
    setActiveType(mapInstance.getMapTypeId());
    return () => listener && listener.remove();
  }, [mapInstance]);

  const styleInline = {
    display: 'flex',
    gap: 0,
    background: 'none',
    border: 'none',
    boxShadow: 'none',
    position: 'static' as const,
    zIndex: 'auto',
  };
  const styleFloating = {
    position: 'absolute' as const,
    left: pos.left,
    top: pos.top,
    background: '#fff',
    border: '1px solid #ccc',
    borderRadius: 3,
    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
    display: 'flex',
    overflow: 'hidden',
    zIndex: 10002,
  };
  // Hide control when Street View is active
  // Try to detect Street View from mapInstance if possible
  const [streetViewActive, setStreetViewActive] = React.useState(false);
  React.useEffect(() => {
    if (!mapInstance) return;
    try {
      const panorama = mapInstance.getStreetView && mapInstance.getStreetView();
      if (panorama) {
        const svListener = panorama.addListener('visible_changed', () => {
          setStreetViewActive(!!(panorama.getVisible && panorama.getVisible()));
        });
        if (panorama.getVisible && panorama.getVisible()) setStreetViewActive(true);
        return () => svListener && svListener.remove();
      }
    } catch (e) {}
  }, [mapInstance]);

  // Hide with inline style and data-hide-in-streetview for consistency
  const uiHiddenStyle = streetViewActive ? { display: 'none' } : undefined;
  return (
    <div
      ref={inline ? undefined : controlRef}
      style={inline ? { ...styleInline, ...uiHiddenStyle } : { ...styleFloating, ...uiHiddenStyle }}
      data-hide-in-streetview
    >
      <button
        onClick={() => { mapInstance && mapInstance.setMapTypeId('roadmap'); }}
        style={{
          background: activeType === 'roadmap' ? '#e8e8e8' : '#fff',
          border: 'none',
          padding: '8px 16px',
          cursor: 'pointer',
          fontWeight: 'bold',
          outline: 'none',
        }}
      >Map</button>
      <button
        onClick={() => { mapInstance && mapInstance.setMapTypeId('satellite'); }}
        style={{
          background: activeType === 'satellite' ? '#e8e8e8' : '#fff',
          border: 'none',
          padding: '8px 16px',
          cursor: 'pointer',
          fontWeight: 'bold',
          outline: 'none',
        }}
      >Satellite</button>
    </div>
  );
}

export default CustomMapTypeControl;
